<template>
	<Modals
		:title="'Code Look Up'"
		:validateBeforeClose="validateBeforeClose"
		:display="display"
		@show="closeGlobalFormModal"
		:parentTag="'form'"
		:novalidate="'novalidate'"
		:fullMobile="true"
	>
		<template #modal-content>
			<div class="code-lookup">
				<div class="code-lookup__header">
					<div class="results-toolbar results-toolbar--variant-one">
						<div class="results-toolbar__search">
							<Search
								variant="toggle-icons"
								:placeholder="placeholder"
								v-model="filters.search"
							/>
						</div>
						<div class="results-toolbar__counter">
							<ResultCounter
								:counter="resultCounter"
								v-if="hasResults"
							/>
						</div>
					</div>
				</div>
			</div>
			<div class="code-lookup__content">
				<Spinner
					:processor="{ loading: loadingSource }"
					:variant="''"
				/>
				<Table
					:sourceObject="snippetsTable"
					:tableMobilev1="true"
					v-if="hasResults"
					@change="updatedPagination"
					@sort="updateDirection"
					:isArrowUp="true"
				>
					<template #body-id="{ body }"
						><span class="selected-item" @click="selectedRow">
							<Radiobutton
								name="rbCodeId"
								:labelText="body.id.toString()"
								:value="body.id"
								@input="getSnippetSelected"
							/> </span
					></template>

					<template #body-snippetAttribute="{ body }">
						<span>{{
							formSnippetAttribute(body.snippetAttribute)
						}}</span>
					</template>

					<template #body-snippetJustify="{ body }">
						<span>{{
							formJustification(body.snippetJustify)
						}}</span>
					</template>

					<!-- DEV NOTES: This a tag should be replaced per router-link to match the mockup of this modal -->

					<!-- DEV NOTES: Popover "top" variation should change to "left" from "md" breakpoint -->
					<template #body-snippetCalc="{ body }">
						<ViewCode
							:popoverText="body.snippetCalc"
							:buttonAttrs="{ labelText: 'View' }"
						/>
					</template>
				</Table>
				<NoResults v-else v-show="!loadingSource" />
			</div>
		</template>
		<template #modal-footer>
			<ButtonGroup
				:disabled="!snippetsDisabled"
				:title="
					!snippetsDisabled
						? 'You don\'t have permission to perform this action.'
						: 'Select code.'
				"
				:items="[
					{
						className: 'btn--primary',
						labelText: 'Select Code',
						btnClick: sendSnippetSelected,
						disabled: hasSnippetSelected
					},
					{
						className: 'btn--link',
						labelText: 'Cancel',
						btnClick: closeGlobalFormModal
					}
				]"
			/>
		</template>
	</Modals>
</template>

<script>
import ResultCounter from '@/site-components/result-counter/result-counter';
import Search from '@/components/forms/search/search';
import Table from '@/components/shells/table/table';
import Modals from '@/components/shells/modal/modals';
import ButtonGroup from '@/components/forms/button/button-group';
import Radiobutton from '@/components/forms/selection-control/radiobutton/radiobutton';
import Spinner from '@/components/visual/spinner/spinner';
import ViewCode from './view-code';
import NoResults from '@/site-components/no-results/no-results';
import { mapActions } from 'vuex';
import { mappingAttribute } from '@/views/shared/snippet-attribute.js';
import _ from 'lodash';

export default {
	name: 'CodeLookUpModal',
	components: {
		Modals,
		Search,
		ButtonGroup,
		ResultCounter,
		Table,
		Radiobutton,
		Spinner,
		ViewCode,
		NoResults
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		title: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: 'Search by code ID or description'
		},
		parentTag: {
			type: String,
			default: 'div'
		},
		validateBeforeClose: {
			type: Boolean,
			default: false
		},
		display: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			filters: {},
			defaultFilters: {
				sortBy: 'id',
				sortDirection: 'asc',
				search: undefined
			},
			paginationFilters: {
				page: 1,
				count: 10
			},
			privilegedUser: true,
			message: 'View',
			snippetsTable: {
				headers: [
					{
						key: 'id',
						label: 'Code Id',
						sorteable: true
					},
					{
						key: 'description',
						label: 'Description',
						sorteable: true
					},
					{
						key: 'snippetAttribute',
						label: 'Attribute',
						sorteable: false
					},
					{
						key: 'snippetFormat',
						label: 'Formatting',
						sorteable: false
					},
					{
						key: 'snippetJustify',
						label: 'Justification',
						sorteable: false
					},
					{
						key: 'snippetCalc',
						label: 'Code',
						sorteable: false
					}
				],
				displayHeader: true,
				source: {}
			},
			snippetSelected: {},
			lastSnipperSelected: null,
			showCode: false
		};
	},
	computed: {
		isConfirmation() {
			return this.type == 'confirmation';
		},
		snippetsDisabled() {
			return this.validatePermission('forms.read');
		},
		resultCounter() {
			return this.snippetsTable.source.totalCount;
		},
		hasResults() {
			return this.snippetsTable.source.totalCount > 0;
		},
		loadingSource() {
			let loading = this.$store.getters['loader/loading']('get.snippets');
			return loading;
		},
		hasSnippetSelected() {
			return !(Object.keys(this.snippetSelected).length > 0);
		}
	},
	methods: {
		...mapActions('codemanager', {
			loadCodeManager: 'loadCodeManager'
		}),

		closeGlobalFormModal(e) {
			if (e) e.preventDefault();
			if (!this.validateBeforeClose) {
				this.snippetsTable.source = {};
				this.snippetSelected = {};
				this.lastSnipperSelected = null;
				this.$emit('show', false);
			}
		},
		fetchData(page) {
			let query = { ...this.filters };
			query.page = page || 1;
			query.count = this.paginationFilters.count || 10;
			this.loadCodeManager(query).then(res => {
				let response = _.cloneDeep(res);
				this.snippetsTable.source = response;
			});
		},
		updatedPagination(val) {
			const data = {
				page: val.page,
				count: val.countPerPage
			};
			if (!_.isEqual(data, this.paginationFilters)) {
				this.paginationFilters = Object.assign(
					{},
					this.paginationFilters,
					data
				);
			}
		},
		updateDirection(val) {
			if (val.sortBy || val.sortDir) {
				const data = {
					sortBy: val.sortBy,
					sortDirection: val.sortDir
				};
				Object.assign(this.filters, data);
			}
		},
		updateToast(val) {
			this.$store.dispatch('toastMessages/show', {
				toastId: 'codelookup.action',
				statusCode: val.status,
				message: val.message,
				slot: 'default',
				useIcon: true,
				userDismissable: true
			});
		},
		selectedRow(event) {
			if (this.lastSnipperSelected)
				this.lastSnipperSelected.classList.remove('is-selected');
			this.lastSnipperSelected =
				event.target.parentElement.parentElement.parentElement.parentElement;
			this.lastSnipperSelected.classList.add('is-selected');
		},
		getSnippetSelected(val) {
			let id = val;
			const { items } = this.snippetsTable.source;
			if (items) {
				const snippet = items.find(s => s.id === id);
				if (snippet) {
					this.snippetSelected = snippet;
				}
			}
		},
		sendSnippetSelected(e) {
			if (e) e.preventDefault();
			let snippet = { ...this.snippetSelected };
			this.$emit('selected', snippet);
			this.closeGlobalFormModal();
		},
		formJustification(value) {
			return value == 'R' ? 'Right' : 'Left';
		},
		formSnippetAttribute(value) {
			return mappingAttribute[value];
		}
	},
	watch: {
		display: {
			handler(val) {
				if (val) {
					this.filters = { ...this.defaultFilters };
				} else {
					this.filters = {};
				}
			}
		},
		filters: {
			deep: true,
			handler() {
				if (this.display) {
					if (this.paginationFilters.page != 1) {
						this.paginationFilters = Object.assign(
							{},
							this.paginationFilters,
							{ page: 1 }
						);
					} else {
						this.fetchData(1);
					}
				}
			}
		},
		paginationFilters: {
			handler(obj) {
				const { page } = obj;
				this.fetchData(page);
			}
		}
	}
};
</script>

<style lang="scss">
/**
  * Code Look Up Modal
**/

.code-lookup {
	&__content {
		font-size: $default-font-size - 2px;
		overflow: auto;
		max-height: calc(100vh - 190px);
		@include scrollbar;
		padding-inline-start: 0;

		@include mq($from: md) {
			max-height: calc(87vh - 320px);
		}
	}
}

.code-field {
	&__view-popover {
		position: relative;
		display: flex;
		align-items: center;
		align-content: center;
		justify-content: center;
		width: max-content;
	}
}
</style>

<style lang="scss" scoped>
::v-deep .results-toolbar__search {
	@include mq($from: md) {
		width: 265px;
	}
}

.modal {
	@include mq($from: md) {
		width: 90%;
	}
	@include mq($from: lg) {
		width: 80%;
	}
}
</style>
